// Sugalarm — Cycle 451.5: reading the meal-photo bolus handoff.
//
// "Use these carbs for this bolus" (PhotoMealScreen) returns to
// `/log?tab=insulin` with `{ bolusPhoto }` in in-memory route state. This
// reads that state ONCE, at LogScreen's mount, so InsulinFlow can mount with
// the meal already in hand (cycle 126 supplied it one render too late, and
// InsulinFlow opened on the path chooser with the meal dropped).
//
// - carbs: the manual Carbs field's own check (`parseCarbGrams`); a payload
//   that fails it, or has no description string or photo provenance, is
//   INVALID and the Insulin tab falls back to the path chooser.
// - units: the string the person typed on the units step before tapping the
//   photo button, carried back BYTE-FOR-BYTE. Nothing here creates, derives
//   or suggests a dose; carbs and the estimate never touch it. If it fails
//   the units step's own check (`parseInsulinUnits`) it becomes "" and the
//   flow opens on the units step, empty.

import { parseCarbGrams, parseInsulinUnits } from "../../lib/logEntryValidation";

// ─── The typed units, one-shot and in memory only ────────────────────────
// Cycle 451.5 (astra): the units the person typed before tapping the photo
// button used to ride the `/log/photo` history entry's state, so Back and
// then Forward to that entry could carry an old dose into a new bolus. They
// now live in this single module slot: armed by InsulinFlow as it leaves
// for the photo screen, TAKEN (consumed) by PhotoMealScreen when it hands
// the meal back, and DISCARDED by every fresh LogScreen mount — so any
// other path (Back, Forward to a stale entry, a reload) finds nothing and
// the flow opens on the units step, empty. Never persisted; never in a URL
// or a history entry.
let armed: { token: string; units: string; label: string } | null = null;
let seq = 0;

/** InsulinFlow, leaving for the photo screen. Returns the token to carry. */
export function armBolusUnits(units: string, label: string): string {
  seq += 1;
  const token = `bolus-${seq}-${Date.now()}`;
  armed = { token, units, label };
  return token;
}

/** PhotoMealScreen, handing the meal back. One-shot: a second take is null. */
export function takeBolusUnits(token: unknown): { units: string; label: string } | null {
  if (!armed || typeof token !== "string" || armed.token !== token) return null;
  const { units, label } = armed;
  armed = null;
  return { units, label };
}

/** LogScreen mount: nothing armed may outlive a return to Log. */
export function discardBolusUnits(): void {
  armed = null;
}

/** The in-progress meal bolus handed back from `/log/photo` (cycle 126). */
export interface BolusPhotoReturn {
  carbs_g: number;
  description: string;
  provenance: Record<string, unknown>;
  /** The person's own typed units, verbatim, or "" when they fail the
   *  units step's check. */
  units: string;
  label: string;
}

export type BolusPhotoHandoff =
  | { kind: "meal"; meal: BolusPhotoReturn }
  | { kind: "invalid" };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Null when the route state carries no handoff at all. */
export function readBolusPhotoHandoff(state: unknown): BolusPhotoHandoff | null {
  if (!isRecord(state) || !("bolusPhoto" in state)) return null;
  const p = state.bolusPhoto;
  if (!isRecord(p)) return { kind: "invalid" };
  const carbs = p.carbs_g;
  if (typeof carbs !== "number" || parseCarbGrams(String(carbs)) === null) {
    return { kind: "invalid" };
  }
  // The photo reference must be a real photo estimate's (astra): anything
  // else is not this handoff.
  if (
    typeof p.description !== "string" ||
    !isRecord(p.provenance) ||
    p.provenance.source !== "photo" ||
    typeof p.provenance.request_id !== "string" ||
    p.provenance.request_id === ""
  ) {
    return { kind: "invalid" };
  }
  const units =
    typeof p.units === "string" && parseInsulinUnits(p.units) !== null ? p.units : "";
  return {
    kind: "meal",
    meal: {
      carbs_g: carbs,
      description: p.description,
      provenance: p.provenance,
      units,
      label: typeof p.label === "string" ? p.label : "",
    },
  };
}
